import { useState } from 'react'
import clsx from 'clsx'
import { ArrowUp, AudioLines, FileText, Loader2, Sparkles, Zap } from 'lucide-react'

import type { AIModel } from '@/api/types'
import {
  MUREKA_PROMPT_MAX,
  resolveMurekaParams,
} from '@/features/studio/canvas/audio-params'
import { ProtoDropdown } from '@/features/studio/components/ui-kit'
import { buildCanvasModelOptions } from '@/features/studio/canvas/model-options'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'

type CanvasT = (key: string, opts?: Record<string, unknown>) => string

const MODE_OPTIONS = [
  { value: 'describe' as const, labelKey: 'canvas.params.musicModeDescribe', icon: Sparkles },
  { value: 'lyrics' as const, labelKey: 'canvas.params.musicModeLyrics', icon: FileText },
]

export function MurekaMusicPanel({
  modelId,
  models,
  params,
  promptLength,
  cost,
  runDisabled,
  runRunning,
  onModelChange,
  onParamsChange,
  onRun,
  t,
}: {
  modelId: string
  models: AIModel[]
  params?: Record<string, string | number>
  promptLength: number
  cost: number
  runDisabled?: boolean
  runRunning?: boolean
  onModelChange: (modelId: string) => void
  onParamsChange: (patch: Record<string, string | number>) => void
  onRun: () => void
  t: CanvasT
}) {
  const resolved = resolveMurekaParams(params)
  const [modeOpen, setModeOpen] = useState(false)
  const modelOptions = buildCanvasModelOptions(models, t)
  const modeOpt = MODE_OPTIONS.find((o) => o.value === resolved.music_mode) ?? MODE_OPTIONS[0]
  const ModeIcon = modeOpt.icon
  const used = Math.min(promptLength, MUREKA_PROMPT_MAX)

  return (
    <div className="cn-gen-controls cn-mureka nodrag nopan nowheel">
      <div className="cn-mureka__bar">
        <span className="cn-mureka__model-icon" aria-hidden>
          <AudioLines className="h-3.5 w-3.5" />
        </span>
        <ProtoDropdown
          compact
          portal
          className="cn-mureka__model"
          value={modelId}
          onValueChange={onModelChange}
          options={modelOptions}
          disabled={!modelOptions.length}
          placeholder={t('canvas.section.model')}
        />

        <Popover open={modeOpen} onOpenChange={setModeOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={clsx('cn-mureka__mode-pill', modeOpen && 'cn-mureka__mode-pill--open')}
            >
              <ModeIcon className="h-3.5 w-3.5 shrink-0 opacity-80" />
              <span className="truncate">{t(modeOpt.labelKey)}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="top"
            className="cn-mureka__mode-pop nodrag nopan nowheel w-[220px] p-2"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="mb-1.5 px-1 text-xs text-muted-foreground">{t('canvas.murekaMusic.modeTitle')}</div>
            {MODE_OPTIONS.map((opt) => {
              const Icon = opt.icon
              const active = resolved.music_mode === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={clsx(
                    'cn-mureka__mode-opt flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm',
                    active && 'cn-mureka__mode-opt--active',
                  )}
                  onClick={() => {
                    onParamsChange({ music_mode: opt.value })
                    setModeOpen(false)
                  }}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" />
                  <span>{t(opt.labelKey)}</span>
                </button>
              )
            })}
          </PopoverContent>
        </Popover>

        <span className="cn-mureka__count" title={t('canvas.murekaMusic.promptLimitHint')}>
          {used}/{MUREKA_PROMPT_MAX}
        </span>
        <span className="cn-mureka__cost" title={t('canvas.modelCost', { cost })}>
          <Zap className="h-3 w-3" />
          {cost}
        </span>
        <button
          type="button"
          className="cn-mureka__run"
          disabled={runDisabled}
          onClick={onRun}
          aria-label={t('canvas.run')}
        >
          {runRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUp className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="cn-mureka__advanced">
        <div className="cn-mureka__advanced-title">{t('canvas.murekaMusic.advanced')}</div>
        <div className="cn-mureka__advanced-row">
          <span>{t('canvas.params.instrumental')}</span>
          <button
            type="button"
            role="switch"
            aria-checked={resolved.instrumental}
            aria-label={t('canvas.params.instrumental')}
            className={clsx('cn-mureka__switch', resolved.instrumental && 'cn-mureka__switch--on')}
            onClick={() =>
              onParamsChange({ instrumental: resolved.instrumental ? 'false' : 'true' })
            }
          />
        </div>
      </div>
    </div>
  )
}
